import {
  Component,
  Input,
  ViewEncapsulation,
  ElementRef,
  ViewChild,
  EventEmitter,
  Output,
  AfterViewChecked,
  forwardRef,
  AfterViewInit,
  OnChanges,
  SimpleChanges
} from '@angular/core';
import { UntypedFormControl, NG_VALIDATORS, NG_VALUE_ACCESSOR, Validator } from '@angular/forms';
import { IMaskDirective  } from 'angular-imask';
import {
  checkFocused,
  checkFocusedout,
  buildIntlTelInput,
  configureIntlTelInputListener,
  toggleFlagContainerVisibility,
  shouldValidate,
  updateMask,
  setInputValue,
  getCountryCodeValue,
  getMaskFromCountryCode
} from './international-number.utils';

import { ControlValueAccessor } from '@angular/forms';
@Component({
  selector: 'tidy-input-international-phone',
  templateUrl: 'input-international-phone.component.html',
  styleUrls: ['./input-international-phone.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputInternationalPhoneComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => InputInternationalPhoneComponent),
      multi: true
    }
  ]
})
export class InputInternationalPhoneComponent implements ControlValueAccessor, AfterViewChecked, AfterViewInit, OnChanges, Validator {
  @ViewChild('phoneInput', {static: false, read: IMaskDirective}) maskDirective: IMaskDirective<any>;
  @ViewChild('phoneInput', {static: false}) phoneInputRef: ElementRef;

  @Input() public label: string;
  @Input() public icon: string;
  @Input() public activeIcon: string;
  @Input() public form: UntypedFormControl;
  @Input() public submitted: boolean;
  @Input() public errorMessage: string;
  @Input() public type: string;
  @Input() public noBorder: boolean;
  @Input() public inputMode: string;
  @Input() autoComplete = false;
  @Input() public initialMask: any;

  focused = false;
  touchedState = false;
  phoneValue: string;
  onChange: (value: string) => void;
  disableElement = false;

  shouldntUpdateCountryInput = false;
  flagsContainer: HTMLElement;
  validateInternationFn = () => { return true };
  @Output() countryCodeChange: EventEmitter<string> = new EventEmitter<string>();
  intlTel: Readonly<any>;
  @Input() defaultCountryCode = 'US';
  setOnCompleteEvent = false;
  setOnAcceptEvent = false;
  shouldUpdateMask = false;
  writeValueChanged = false;
  maskInitialized = false;

  ngAfterViewInit() {
    if (this.initialMask && !this.maskInitialized && this.maskDirective && this.phoneInputRef) {
      this.initIntLTel();
      this.maskInitialized = true;
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.initialMask && changes.initialMask.currentValue && !this.maskInitialized && this.maskDirective && this.phoneInputRef) {
      this.initIntLTel();
      this.maskInitialized = true;
    }
  }

  initIntLTel() {
    // Check if both maskDirective and phoneInputRef are available
    if (!this.maskDirective || !this.phoneInputRef) {
      console.warn('Mask directive or phone input not available yet');
      return;
    }

    const maskRef = this.maskDirective.maskRef;
    const input = this.phoneInputRef.nativeElement;

    if (!input) {
      console.warn('Phone input element not available');
      return;
    }

    this.intlTel = buildIntlTelInput(input, this.defaultCountryCode);
    configureIntlTelInputListener(input, this.intlTel, this.countryCodeChange, maskRef);
    this.flagsContainer = this.intlTel.countryContainer;
    this.validateInternationFn = shouldValidate.bind(null, this.intlTel);
    /* toggleFlagContainerVisibility('none', this.flagsContainer); */

    // Update mask to the correct one based on the dial code from intlTelInput
    if (maskRef && getCountryCodeValue(this.intlTel)) {
      updateMask(this.intlTel, maskRef);
      this.shouldUpdateMask = true;
    }

    this.countryCodeChange.emit(getCountryCodeValue(this.intlTel));
  }

  ngAfterViewChecked() {
    // Try to initialize if not already done and dependencies are available
    if (!this.maskInitialized && this.initialMask && this.maskDirective && this.phoneInputRef) {
      this.initIntLTel();
      this.maskInitialized = true;
    }

    if (this.maskInitialized) {
      this.configureCountrySelection();
    }
  }

  configureCountrySelection() {
    if (!this.maskDirective || !this.maskDirective.maskRef) {
      return;
    }

    const maskRef = this.maskDirective.maskRef;

    if (!this.shouldntUpdateCountryInput && maskRef.unmaskedValue && this.writeValueChanged) {
      toggleFlagContainerVisibility('block', this.flagsContainer);
      setInputValue(this.intlTel, `+${maskRef.unmaskedValue}`);

      this.shouldntUpdateCountryInput = true;
    }

    if (!this.setOnCompleteEvent && maskRef.unmaskedValue) {
      maskRef.on('complete', this.handleMaskCompleteEvent.bind(this));

      this.setOnCompleteEvent = true;
    }

    // Listen to input changes to ensure form control always has the full international number
    if (!this.setOnAcceptEvent && maskRef) {
      maskRef.on('accept', () => {
        this.updateFormControlValue();
      });
      this.setOnAcceptEvent = true;
    }

    if (!this.shouldUpdateMask && getCountryCodeValue(this.intlTel)) {
      updateMask(this.intlTel, maskRef);

      this.shouldUpdateMask = true;
    }
  }

  updateFormControlValue() {
    if (!this.intlTel || !this.maskDirective?.maskRef || !this.onChange) {
      return;
    }

    const unmaskedValue = this.maskDirective.maskRef.unmaskedValue;
    if (!unmaskedValue) {
      // Clear the form control if no value
      this.onChange('');
      return;
    }

    // Construct the full international number from mask value and country code
    // This ensures the form control always has the correct format (e.g., +14248551768)
    // The onChange callback only updates the form control, it doesn't modify the input element
    const countryData = this.intlTel.getSelectedCountryData();
    const dialCode = countryData?.dialCode || '';
    const fullNumber = dialCode ? `+${dialCode}${unmaskedValue}` : `+${unmaskedValue}`;

    // Update the form control with the full international number format
    this.onChange(fullNumber);
  }

  validate(_: UntypedFormControl) {
    // Only validate if intlTel is initialized
    if (!this.intlTel) {
      return null;
    }

    // Get the unmasked value from the mask - this is the actual digits the user typed
    const unmaskedValue = this.maskDirective?.maskRef?.unmaskedValue || '';

    // Don't validate if there's no number entered
    if (!unmaskedValue) {
      return null;
    }

    // Get the country code from intlTelInput
    const countryData = this.intlTel.getSelectedCountryData();
    const dialCode = countryData?.dialCode || '';

    // Construct the full international number for validation
    const fullNumber = dialCode ? `+${dialCode}${unmaskedValue}` : `+${unmaskedValue}`;
    const digitsOnly = fullNumber.replace(/\D/g, '');

    // For non-US numbers, isValidNumber() might fail if utils script isn't loaded yet
    // or if intlTelInput isn't synced with the mask. We'll validate based on format first.
    // Check if the number has a reasonable length (country code + at least 7 digits)
    const hasMinimumLength = dialCode && digitsOnly.length >= (dialCode.length + 7);

    if (!hasMinimumLength) {
      // Number is too short - invalid
      const invalidErrorObject = {
        phone: { valid: false }
      };
      return invalidErrorObject;
    }

    // Number has minimum length, now try to use intlTelInput's validation if available
    // But only if intlTelInput is synced (to avoid modifying input during typing)
    const intlTelNumber = this.intlTel.getNumber();
    const isSynced = intlTelNumber && intlTelNumber.replace(/\D/g, '') === digitsOnly;

    if (isSynced) {
      // intlTelInput is in sync, use its validation
      const isValid = this.intlTel.isValidNumber();
      if (!isValid) {
        // Even though format looks good, intlTelInput says it's invalid
        // This could be due to utils script not loaded, but we'll trust intlTelInput
        // for US numbers (which work without utils script)
        const isUS = dialCode === '1';
        if (isUS) {
          // For US, trust intlTelInput validation
          const invalidErrorObject = {
            phone: { valid: false }
          };
          return invalidErrorObject;
        }
        // For non-US, allow it if format looks good (utils script might not be loaded)
        return null;
      }
      return null; // Valid
    } else {
      // intlTelInput not synced, but format looks good - allow it
      // Server-side validation will catch truly invalid numbers
      return null;
    }
  }

  checkFocused() {
    checkFocused(this.flagsContainer);
  }

  checkFocusedout(e) {
    const isEmpty = e.target.value === '';

    checkFocusedout(e, this.flagsContainer, isEmpty);

    // Update form control value on blur to ensure it's correct when user finishes typing
    this.updateFormControlValue();
  }

  changeTouch = () => {
    this.touchedState = true;
  }

  setFocused(status) {
    this.focused = status;
  }

  ngOnDestroy() {
    this.intlTel?.destroy();
  }

  writeValue(value: string) {
    if (this.intlTel) {
      setInputValue(this.intlTel, value);
    }

    this.writeValueChanged = !!value?.includes('+');

    this.phoneValue = value;
  }

  registerOnChange(fn: (value: string) => void) {
    this.onChange = fn;
  }

  handleMaskCompleteEvent() {
    // Update form control value when mask is complete
    this.updateFormControlValue();
  }

  registerOnTouched() {}

  setDisabledState(isDisabled: boolean) {
    this.disableElement = isDisabled;
  }

  get normalizedMask() {
    // If initialMask is provided (as mask object or country code), convert it to a mask object
    // Otherwise use generic default mask - the correct mask will be set by updateMask() after intlTelInput initializes
    const currentCountryCode = getCountryCodeValue(this.intlTel);
    return getMaskFromCountryCode(currentCountryCode);
  }
}
